import { useSubsequentEffect } from '@carvana/merch-react-hooks';
import React, { useContext, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import getScrollBarSize from '~/utilities/getScrollBarSize';
import isServerSideRendering from '~/utilities/isServerSideRendering';
import PortalContext from './PortalContext';

type OwnProps = React.PropsWithChildren<{
  id: string;
  open: boolean;
  target?: HTMLElement;
}>;

// Locks the body scroll when a portal is open and factors in the scrollbar
// width to prevent content from shifting. Only set the scrollbar width property
// if it doesn't already exist.
export function useBodyScrollLock(isOpen: boolean) {
  const scrollbarWidthProp = useRef('');

  useSubsequentEffect(() => {
    const body = document.body;

    if (isOpen) {
      body.classList.add('no-scroll', 'm:pr-[var(--scrollbar-width)]');

      scrollbarWidthProp.current =
        body.style.getPropertyValue('--scrollbar-width');

      if (!scrollbarWidthProp.current) {
        body.style.setProperty('--scrollbar-width', `${getScrollBarSize()}px`);
      }

      return;
    }

    body.classList.remove('no-scroll', 'm:pr-[var(--scrollbar-width)]');

    if (!scrollbarWidthProp.current) {
      body.style.removeProperty('--scrollbar-width');
    }
  }, [isOpen]);
}

export default function Portal({
  children,
  id,
  open,
  target = isServerSideRendering
    ? undefined
    : document.querySelector('main') ?? document.body
}: OwnProps) {
  const { closePortal, hasOpenPortals, openPortal } = useContext(PortalContext);
  const toggle = open ? openPortal : closePortal;

  useEffect(() => {
    toggle(id);
  }, [id, toggle]);

  useBodyScrollLock(hasOpenPortals);

  // Remove no-scroll when Portal unmounts in the case Portal is wrapped in an error boundary
  useEffect(
    () => () => {
      document.body.classList.remove('no-scroll');
    },
    []
  );

  return open && target ? createPortal(children, target) : null;
}
